import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PreferencesProvider } from "@/hooks/usePreferences";
import { PreferencesPanel } from "@/components/PreferencesPanel";
import { TimerProvider } from "@/hooks/useTimers";
import { TimerDock } from "@/components/TimerDock";
import { IosInstallPrompt } from "@/components/IosInstallPrompt";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { supabase } from "@/integrations/supabase/client";
import { useKeepSessionAlive } from "@/hooks/useKeepSessionAlive";
// Index (the landing page) is the LCP-critical route, so it stays eagerly
// imported. Every other route is split into its own chunk via React.lazy
// so the initial JS bundle drops from ~417 KiB (with ~313 KiB unused on the
// landing page) to just what's needed for first paint. This is the single
// biggest remaining LCP win after the Stripe.js deferral.
import Index from "./pages/Index.tsx";
import ShoppingList from "./pages/ShoppingList.tsx";
import Account from "./pages/Account.tsx";
const Recipe = lazy(() => import("./pages/Recipe.tsx"));
const SharedRecipe = lazy(() => import("./pages/SharedRecipe.tsx"));
const MyRecipes = lazy(() => import("./pages/MyRecipes.tsx"));
const Menus = lazy(() => import("./pages/Menus.tsx"));
const MenuDetail = lazy(() => import("./pages/MenuDetail.tsx"));
const MenuPrint = lazy(() => import("./pages/MenuPrint.tsx"));
const Upgrade = lazy(() => import("./pages/Upgrade.tsx"));
const UpgradeExpired = lazy(() => import("./pages/UpgradeExpired.tsx"));
const Demo = lazy(() => import("./pages/Demo.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

/**
 * Strip Supabase auth tokens from `window.location.hash` once the session
 * is established. Without this, after clicking an email-verification link
 * users land on e.g. `/my-recipes#access_token=...&type=signup` and the
 * fragment lingers indefinitely. That broke premium upsells on iPhone:
 * subsequent navigations to `/upgrade` could end up with the auth hash
 * still present, and the Stripe iframe rendered as a blank backdrop.
 *
 * We use `replaceState` (no reload) and only act when an auth fragment is
 * actually present, so this is a no-op for normal navigations.
 */
const AuthHashCleanup = () => {
  useEffect(() => {
    const hasAuthHash = () => {
      const h = window.location.hash || "";
      return (
        h.includes("access_token=") ||
        h.includes("refresh_token=") ||
        h.includes("type=signup") ||
        h.includes("type=recovery") ||
        h.includes("type=magiclink") ||
        h.includes("type=invite")
      );
    };

    const clean = () => {
      if (!hasAuthHash()) return;
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", url);
    };

    // If the session is already restored on mount, clean immediately.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) clean();
    });

    // Otherwise wait for the SIGNED_IN / INITIAL_SESSION event Supabase
    // emits after parsing the URL hash, then clean.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) clean();
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  return null;
};

const SessionKeepAlive = () => {
  useKeepSessionAlive();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter>
        <PreferencesProvider>
          <TimerProvider>
            <AuthHashCleanup />
            <SessionKeepAlive />
            <PaymentTestModeBanner />
            <ErrorBoundary>
              <Suspense
                fallback={
                  <div
                    aria-busy="true"
                    className="flex min-h-[60vh] items-center justify-center"
                  >
                    <div className="relative h-10 w-10">
                      <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
                      <div className="absolute inset-2 animate-pulse rounded-full bg-primary/40" />
                      <div className="absolute inset-[14px] rounded-full bg-primary" />
                    </div>
                  </div>
                }
              >
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/recipe" element={<Recipe />} />
                  <Route path="/r/:shareId" element={<SharedRecipe />} />
                  <Route path="/my-recipes" element={<MyRecipes />} />
                  <Route path="/menus" element={<Menus />} />
                  <Route path="/menus/:id" element={<MenuDetail />} />
                  <Route path="/menus/:id/list" element={<ShoppingList />} />
                  <Route path="/menus/:id/print" element={<MenuPrint />} />
                  <Route path="/upgrade" element={<Upgrade />} />
                  <Route path="/upgrade/expired" element={<UpgradeExpired />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/demo" element={<Demo />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <PreferencesPanel />
            <TimerDock />
            <IosInstallPrompt />
            <FeedbackWidget />
          </TimerProvider>
        </PreferencesProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
